import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import ClientFinanceDrawer from '../components/finance/ClientFinanceDrawer';
import InvoiceBuilder from '../components/finance/InvoiceBuilder';
import InvoiceDrawer from '../components/finance/InvoiceDrawer';
// ⚠️ ONE EDITOR, TWO HOSTS — the PlanChooser rule. This tab and the Retainers
// section render the same component, so a retainer edited from either place
// gets the same service picker, the same negotiated-fee handling and the same
// agreement generation. The finance-local RetainerModal it replaced is gone:
// two editors for one arrangement is exactly the drift this codebase keeps
// paying for.
import RetainerEditor from '../components/retainers/RetainerEditor';
import PaymentReviewQueue from '../components/finance/PaymentReviewQueue';
import FinanceSettingsCard from '../components/finance/FinanceSettingsCard';
import { Button, Card, Select, Skeleton, Tabs, useToast } from '../../design-system';
import '../styles/Finance.css';

// The firm's money.
//
// Five views over the same data: the headline position, who owes what, the
// invoices themselves, work that has never been billed, and the standing fixed
// fees. Everything drills to a client or an invoice, because those are the two
// things you actually act on.

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'clients', label: 'By client' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'advices', label: 'Payments to review' },
  { key: 'unbilled', label: 'Unbilled work' },
  { key: 'retainers', label: 'Retainers' },
  { key: 'settings', label: 'Settings' },
];

export const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
export const moneyShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(1)}k`;
  return `₹${v.toFixed(0)}`;
};
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

// How long something has been waiting, for the FIFO lists. Days up to a month,
// then months — "94 days" is harder to read at a glance than "3 months".
export const ageLabel = (days) => {
  if (days == null) return '—';
  if (days === 0) return 'today';
  if (days < 31) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months} mo`;
};

export const INV_STATUS = {
  draft: { label: 'Draft', cls: 'fin-s-draft' },
  proforma_sent: { label: 'Proforma sent', cls: 'fin-s-proforma' },
  issued: { label: 'Issued', cls: 'fin-s-issued' },
  partly_paid: { label: 'Part paid', cls: 'fin-s-part' },
  paid: { label: 'Paid', cls: 'fin-s-paid' },
  cancelled: { label: 'Cancelled', cls: 'fin-s-cancelled' },
};

const INVOICE_VIEWS = [
  { value: 'all', label: 'All invoices' },
  { value: 'pending', label: 'Pending payment' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'proforma', label: 'Proformas awaiting payment' },
];

function OrgAdminFinance() {
  const toast = useToast();
  // Deep links: a task's Billing card sends people here with the client or the
  // invoice it just made, so they land on the row they came for rather than on
  // the Overview with a search to do.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get('tab');
    return TABS.some((x) => x.key === t) ? t : 'overview';
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((n) => n + 1);

  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [unbilled, setUnbilled] = useState(null);
  const [retainers, setRetainers] = useState(null);
  const [loading, setLoading] = useState(true);

  const [invoiceView, setInvoiceView] = useState('all');
  const [search, setSearch] = useState('');

  const [drawerClient, setDrawerClient] = useState(null);
  const [drawerInvoice, setDrawerInvoice] = useState(null);
  const [building, setBuilding] = useState(null);
  const [editingRetainer, setEditingRetainer] = useState(null);
  // Which client's charges are expanded on the Unbilled tab, and which of them
  // are ticked for the next invoice.
  const [openClient, setOpenClient] = useState(searchParams.get('client') || null);
  const [picked, setPicked] = useState(() => new Set());

  // Consume the deep-link params ONCE. Left in the URL they would fight the
  // user: closing the invoice drawer would immediately reopen it on the next
  // render, and switching tabs would snap back.
  useEffect(() => {
    const invoiceId = searchParams.get('invoice');
    const clientId = searchParams.get('client');
    if (invoiceId) setDrawerInvoice(invoiceId);
    // On the By-client tab, `client` means "open this client's drawer" — that is
    // where the Billing report's client links land, so a partner reading a
    // number in Reports arrives at the invoices and receipts behind it rather
    // than at a table they still have to search. On the Unbilled tab the same
    // param expands that client's charges instead (see openClient above).
    if (clientId && searchParams.get('tab') === 'clients') setDrawerClient(clientId);
    if (searchParams.get('tab') || invoiceId || clientId) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The review queue fetches its own data (it has its own status filter),
      // so the shared loader has nothing to do for that tab.
      if (tab === 'advices' || tab === 'settings') { setLoading(false); return; }
      const params = tab === 'invoices' ? { view: invoiceView, search } : {};
      const url = {
        overview: '/api/finance/overview',
        clients: '/api/finance/by-client',
        invoices: '/api/finance/invoices',
        unbilled: '/api/finance/unbilled',
        retainers: '/api/finance/retainers',
      }[tab];
      const r = await apiClient.get(url, { params });
      ({
        overview: setOverview, clients: setClients, invoices: setInvoices,
        unbilled: setUnbilled, retainers: setRetainers,
      })[tab](r.data);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not load finance data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, invoiceView, search, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const t = overview?.totals;

  return (
    <OrgAdminLayout
      title="Finance"
      subtitle={overview ? `FY ${overview.financialYear} · billed, collected and outstanding` : 'Billing, receipts and outstanding'}
      headerRight={<Button variant="primary" onClick={() => setBuilding({})}>+ New invoice</Button>}
    >
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {/* ── Overview ── */}
      {tab === 'overview' && (
        loading || !overview ? <Card><Skeleton.Text lines={8} /></Card> : (
          <div className="fin-stack">
            <div className="fin-tiles">
              <div className="fin-tile">
                <span className="fin-tile-label">Billed this FY</span>
                <span className="fin-tile-value">{money(t.billed)}</span>
                {t.draftValue > 0 && <span className="fin-tile-hint">{money(t.draftValue)} still in draft</span>}
              </div>
              <div className="fin-tile fin-tile-good">
                <span className="fin-tile-label">Settled</span>
                <span className="fin-tile-value">{money(t.settled)}</span>
                <span className="fin-tile-hint">{money(t.collected)} banked + {money(t.tdsWithheld)} TDS</span>
              </div>
              <div className="fin-tile fin-tile-warn">
                <span className="fin-tile-label">Outstanding</span>
                <span className="fin-tile-value">{money(t.outstanding)}</span>
                <span className="fin-tile-hint">across {t.openCount} open invoice{t.openCount === 1 ? '' : 's'}</span>
              </div>
              <div className="fin-tile fin-tile-bad">
                <span className="fin-tile-label">Overdue</span>
                <span className="fin-tile-value">{money(t.overdue)}</span>
                <span className="fin-tile-hint">{t.overdueCount} past their due date</span>
              </div>
              {/* Its own tile, not folded into billed or outstanding: a
                  proforma is neither. Showing it nowhere would make sent
                  proformas invisible on the page about money. */}
              {t.proformaCount > 0 && (
                <div className="fin-tile">
                  <span className="fin-tile-label">Proformas awaiting payment</span>
                  <span className="fin-tile-value">{money(t.proformaValue)}</span>
                  <span className="fin-tile-hint">
                    {t.proformaCount} sent · not yet billed or owed
                  </span>
                </div>
              )}
            </div>

            <Card>
              <h3 className="fin-h">Receivables ageing</h3>
              <p className="fin-sub">How long the money you're owed has been owed. Anything past 60 days needs a call.</p>
              <div className="fin-age">
                {Object.entries(overview.ageing).map(([bucket, v]) => {
                  const max = Math.max(1, ...Object.values(overview.ageing).map((x) => x.amount));
                  return (
                    <div className="fin-age-row" key={bucket}>
                      <span className="fin-age-label">{bucket === 'current' ? 'Not yet due' : `${bucket} days`}</span>
                      <div className="fin-age-track">
                        <div
                          className={`fin-age-fill fin-age-${bucket.replace('+', 'plus')}`}
                          style={{ width: `${(v.amount / max) * 100}%` }}
                        />
                      </div>
                      <span className="fin-age-amt">{money(v.amount)}</span>
                      <span className="fin-age-count">{v.count}</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card>
              <h3 className="fin-h">Billed vs collected</h3>
              <div className="fin-bars">
                {overview.byMonth.length === 0 ? <p className="fin-empty">No activity yet.</p> : (() => {
                  const max = Math.max(1, ...overview.byMonth.flatMap((m) => [m.billed, m.collected]));
                  return overview.byMonth.map((m) => (
                    <div className="fin-bar-col" key={m.key} title={`${m.key}: billed ${money(m.billed)}, collected ${money(m.collected)}`}>
                      <div className="fin-bar-pair">
                        <div className="fin-bar fin-bar-billed" style={{ height: `${(m.billed / max) * 100}%` }} />
                        <div className="fin-bar fin-bar-collected" style={{ height: `${(m.collected / max) * 100}%` }} />
                      </div>
                      <span className="fin-bar-x">{m.key.slice(5)}</span>
                    </div>
                  ));
                })()}
              </div>
              <div className="fin-legend">
                <span><i className="fin-sw fin-bar-billed" /> Billed</span>
                <span><i className="fin-sw fin-bar-collected" /> Collected</span>
              </div>
            </Card>
          </div>
        )
      )}

      {/* ── By client ── */}
      {tab === 'clients' && (
        <Card>
          <h3 className="fin-h">Each client's open position</h3>
          <p className="fin-sub">
            Every client with money anywhere in the system — invoiced, awaiting payment, sent as a
            proforma, or priced on a task and not yet invoiced. Click one for their full history.
            {' '}
            {/* Without this the two columns look like they disagree. They don't:
                they measure different things. */}
            <b>Charged on tasks</b> is what the work was priced at, before tax; <b>Invoiced</b> is the
            document total including GST — so the two are not meant to match.
          </p>
          {loading || !clients ? <Skeleton.Text lines={6} /> : (
            <div className="fin-scroll">
              <table className="fin-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="fin-num">Charged on tasks</th>
                    <th className="fin-num">Invoiced</th>
                    <th className="fin-num">Proforma</th>
                    <th className="fin-num">Received</th>
                    <th className="fin-num">TDS</th>
                    <th className="fin-num">Unpaid</th>
                    <th className="fin-num">Overdue</th>
                    <th className="fin-num">Not invoiced</th>
                    <th>Oldest</th>
                    <th>Last billed</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.rows.map((c) => (
                    <tr key={c.clientId} className="fin-row-click" onClick={() => setDrawerClient(c.clientId)}>
                      <td>
                        <span className="fin-name">{c.name}</span>
                        {c.code && <span className="fin-code">{c.code}</span>}
                        {c.draft > 0 && <span className="fin-chip fin-s-draft">{money(c.draft)} draft</span>}
                      </td>
                      {/* What the WORK says they owe, before any invoice exists —
                          the honest starting point, and the number people expect
                          to reconcile the rest against. */}
                      <td className="fin-num">
                        {c.chargesTotal
                          ? <span title={`${c.chargesCount} charge${c.chargesCount === 1 ? '' : 's'} across their tasks`}>{money(c.chargesTotal)}</span>
                          : '—'}
                      </td>
                      <td className="fin-num">{c.billed ? money(c.billed) : '—'}</td>
                      {/* Neither revenue nor a receivable — its own column, or it
                          would have to be folded into one of those and lie. */}
                      <td className="fin-num fin-muted">{c.proforma ? money(c.proforma) : '—'}</td>
                      <td className="fin-num">{c.collected ? money(c.collected) : '—'}</td>
                      <td className="fin-num fin-muted">{c.tdsCredited ? money(c.tdsCredited) : '—'}</td>
                      <td className="fin-num">{c.outstanding ? <b>{money(c.outstanding)}</b> : '—'}</td>
                      <td className="fin-num">{c.overdue ? <b className="fin-bad">{money(c.overdue)}</b> : '—'}</td>
                      <td className="fin-num">
                        {c.unbilledCharges
                          ? <b className="fin-warn" title={`${c.unbilledCount} charge${c.unbilledCount === 1 ? '' : 's'} on tasks, not on any invoice`}>{money(c.unbilledCharges)}</b>
                          : '—'}
                      </td>
                      {/* FIFO: how long their longest-waiting uninvoiced charge
                          has sat there. */}
                      <td className="fin-nowrap fin-muted">
                        {c.oldestUnbilledAt ? ageLabel(Math.floor((Date.now() - new Date(c.oldestUnbilledAt)) / 86400000)) : '—'}
                      </td>
                      <td className="fin-muted">{fmtDate(c.lastInvoiceAt)}</td>
                    </tr>
                  ))}
                  {clients.rows.length === 0 && (
                    <tr><td colSpan={11}><p className="fin-empty">No client has any billing activity yet.</p></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Invoices ── */}
      {tab === 'invoices' && (
        <Card>
          <div className="fin-toolbar">
            <Select value={invoiceView} onChange={(e) => setInvoiceView(e.target.value)} aria-label="View">
              {INVOICE_VIEWS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </Select>
            <input
              className="fin-search" placeholder="🔎 Invoice number or client…"
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
            {invoices && (
              <span className="fin-count">
                {invoices.totals.count} · {money(invoices.totals.total)}
                {invoices.totals.due > 0 && <> · <b>{money(invoices.totals.due)} due</b></>}
              </span>
            )}
          </div>

          {loading || !invoices ? <Skeleton.Text lines={6} /> : (
            <div className="fin-scroll">
              <table className="fin-table">
                <thead>
                  <tr>
                    <th>Invoice</th><th>Client</th><th>Date</th><th>Due</th>
                    <th className="fin-num">Total</th>
                    <th className="fin-num">Due</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.rows.map((i) => {
                    const s = INV_STATUS[i.status];
                    return (
                      <tr key={i.id} className="fin-row-click" onClick={() => setDrawerInvoice(i.id)}>
                        <td className="fin-nowrap"><b>{i.invoiceNumber || 'Draft'}</b></td>
                        <td>{i.clientName}</td>
                        <td className="fin-nowrap fin-muted">{fmtDate(i.invoiceDate)}</td>
                        <td className="fin-nowrap fin-muted">
                          {fmtDate(i.dueDate)}
                          {i.isOverdue && <span className="fin-chip fin-s-overdue">{i.ageBucket}d</span>}
                        </td>
                        <td className="fin-num">{money(i.total)}</td>
                        <td className="fin-num">{i.amountDue ? <b>{money(i.amountDue)}</b> : '—'}</td>
                        <td><span className={`fin-pill ${s.cls}`}>{s.label}</span></td>
                      </tr>
                    );
                  })}
                  {invoices.rows.length === 0 && (
                    <tr><td colSpan={7}><p className="fin-empty">No invoices match.</p></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Unbilled ── */}
      {tab === 'advices' && <PaymentReviewQueue onChanged={bump} />}

      {tab === 'settings' && <FinanceSettingsCard />}

      {tab === 'unbilled' && (
        loading || !unbilled ? <Card><Skeleton.Text lines={6} /></Card> : (
          <div className="fin-stack">
            <div className="fin-tiles">
              <div className="fin-tile fin-tile-warn">
                <span className="fin-tile-label">Priced but not invoiced</span>
                <span className="fin-tile-value">{money(unbilled.totals.chargeValue)}</span>
                <span className="fin-tile-hint">
                  {unbilled.totals.chargeCount} charge{unbilled.totals.chargeCount === 1 ? '' : 's'} waiting
                  {' · listed below'}
                </span>
              </div>
              {/* Charges on a task with no client. Counted separately so this
                  page's headline always equals the sum of its lists — folding
                  them into the tile above is what made "₹20,000 waiting" sit over
                  an empty table. */}
              {unbilled.totals.orphanCount > 0 && (
                <div className="fin-tile fin-tile-bad">
                  <span className="fin-tile-label">Can't be invoiced yet</span>
                  <span className="fin-tile-value">{money(unbilled.totals.orphanValue)}</span>
                  <span className="fin-tile-hint">
                    {unbilled.totals.orphanCount} charge{unbilled.totals.orphanCount === 1 ? '' : 's'} on a task with no client
                  </span>
                </div>
              )}
              <div className="fin-tile fin-tile-bad">
                <span className="fin-tile-label">Completed but never priced</span>
                <span className="fin-tile-value">{unbilled.totals.unpricedCount}</span>
                <span className="fin-tile-hint">tasks finished with no charge at all</span>
              </div>
            </div>

            <Card>
              <h3 className="fin-h">Ready to invoice — oldest first</h3>
              <p className="fin-sub">
                Charges recorded on tasks that haven't reached an invoice, in the order the work was
                done (FIFO): the client carrying the longest-waiting charge is at the top. Open a
                client to see the individual charges and tick the ones this invoice should cover — an
                invoice normally gathers the month's work rather than one task at a time.
              </p>
              <div className="fin-scroll">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Client</th><th className="fin-num">Charges</th>
                      <th className="fin-num">Value</th><th>Waiting</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {unbilled.byClient.map((g) => {
                      const rows = unbilled.charges.filter((c) => c.clientId === g.clientId);
                      const isOpen = openClient === g.clientId;
                      // Nothing ticked means "all of them" — the common case, and
                      // it keeps the button honest when the list is collapsed.
                      const chosen = rows.filter((c) => picked.has(c.billItemId));
                      const billing = chosen.length ? chosen : rows;
                      const value = billing.reduce((s, c) => s + c.amount, 0);
                      return (
                        <Fragment key={g.clientId}>
                          <tr>
                            <td>
                              <button
                                type="button" className="fin-expander"
                                aria-expanded={isOpen}
                                onClick={() => { setOpenClient(isOpen ? null : g.clientId); setPicked(new Set()); }}
                              >
                                <span className={`fin-caret${isOpen ? ' is-open' : ''}`} aria-hidden="true">▸</span>
                                <span className="fin-name">{g.name}</span>
                                {g.code && <span className="fin-code">{g.code}</span>}
                              </button>
                            </td>
                            <td className="fin-num fin-muted">{g.count}</td>
                            <td className="fin-num"><b>{money(g.amount)}</b></td>
                            {/* The FIFO handle: how long the OLDEST charge here
                                has gone unbilled, which is why this row is where
                                it is in the list. */}
                            <td className="fin-nowrap">{ageLabel(g.oldestAgeDays)}</td>
                            <td className="fin-num">
                              <Button
                                variant="primary" size="sm"
                                onClick={() => setBuilding({ clientId: g.clientId, charges: billing })}
                              >
                                {chosen.length && chosen.length !== rows.length
                                  ? `Invoice ${money(value)} (${chosen.length})`
                                  : 'Raise invoice'}
                              </Button>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="fin-subrow">
                              <td colSpan={5}>
                                <table className="fin-table fin-table-inner">
                                  <thead>
                                    <tr>
                                      <th className="fin-tick" />
                                      <th>Charge</th><th>Task</th><th>Head</th>
                                      <th>Raised</th><th className="fin-num">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((c) => (
                                      <tr key={c.billItemId}>
                                        <td className="fin-tick">
                                          <input
                                            type="checkbox"
                                            checked={picked.has(c.billItemId)}
                                            aria-label={`Include ${c.description}`}
                                            onChange={(e) => setPicked((prev) => {
                                              const next = new Set(prev);
                                              if (e.target.checked) next.add(c.billItemId);
                                              else next.delete(c.billItemId);
                                              return next;
                                            })}
                                          />
                                        </td>
                                        <td>{c.description}</td>
                                        <td>
                                          <Link className="fin-link" to={`/org-admin/tasks/${c.taskId}`}>
                                            {c.taskNumber != null ? `TASK-${String(c.taskNumber).padStart(4, '0')}` : c.title}
                                          </Link>
                                          {c.period && <span className="fin-code">{c.period}</span>}
                                        </td>
                                        <td className="fin-muted">{c.kind === 'ope' ? 'Reimbursement' : 'Fees'}</td>
                                        <td className="fin-muted fin-nowrap">
                                          {fmtDate(c.raisedAt)}
                                          <span className="fin-code">{ageLabel(c.ageDays)}</span>
                                        </td>
                                        <td className="fin-num">{money(c.amount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                    {unbilled.byClient.length === 0 && (
                      <tr><td colSpan={5}><p className="fin-empty">
                        {unbilled.totals.orphanCount > 0
                          ? 'Nothing here can be invoiced — every waiting charge is on a task with no client. See below.'
                          : 'Everything priced has been invoiced. 🎉'}
                      </p></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Charges that cannot be invoiced at all until someone puts a client
                on the task. Previously counted in the headline and shown nowhere,
                which is what made the total look wrong. */}
            {unbilled.orphans?.length > 0 && (
              <Card>
                <h3 className="fin-h">Waiting on a client</h3>
                <p className="fin-sub">
                  These charges are priced but the task names no client, so there is nobody to invoice.
                  Open the task and set its client — they will move into the list above.
                </p>
                <div className="fin-scroll">
                  <table className="fin-table">
                    <thead>
                      <tr><th>Charge</th><th>Task</th><th>Head</th><th>Raised</th><th className="fin-num">Amount</th></tr>
                    </thead>
                    <tbody>
                      {unbilled.orphans.map((c) => (
                        <tr key={c.billItemId}>
                          <td>{c.description}</td>
                          <td>
                            <Link className="fin-link" to={`/org-admin/tasks/${c.taskId}`}>
                              {c.taskNumber != null ? `TASK-${String(c.taskNumber).padStart(4, '0')}` : c.title}
                            </Link>
                          </td>
                          <td className="fin-muted">{c.kind === 'ope' ? 'Reimbursement' : 'Fees'}</td>
                          <td className="fin-muted fin-nowrap">
                            {fmtDate(c.raisedAt)}
                            <span className="fin-code">{ageLabel(c.ageDays)}</span>
                          </td>
                          <td className="fin-num">{money(c.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            <Card>
              <h3 className="fin-h">Completed with no charge</h3>
              <p className="fin-sub">
                Work that finished without anything billed against it — the commonest kind of revenue leakage.
                Add a bill on the task, or leave it if it was genuinely free.
              </p>
              <div className="fin-scroll">
                <table className="fin-table">
                  <thead><tr><th>Task</th><th>Client</th><th>Period</th><th>Completed</th></tr></thead>
                  <tbody>
                    {unbilled.unpriced.slice(0, 100).map((u) => (
                      <tr key={u.taskId}>
                        <td>
                          <a className="fin-link" href={`/org-admin/tasks/${u.taskId}`}>{u.title}</a>
                          {u.taskNumber != null && <span className="fin-code">TASK-{String(u.taskNumber).padStart(4, '0')}</span>}
                        </td>
                        <td>{u.clientName}</td>
                        <td className="fin-muted">{u.period || '—'}</td>
                        <td className="fin-muted fin-nowrap">{fmtDate(u.completedAt)}</td>
                      </tr>
                    ))}
                    {unbilled.unpriced.length === 0 && (
                      <tr><td colSpan={4}><p className="fin-empty">Nothing unpriced.</p></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )
      )}

      {/* ── Retainers ── */}
      {tab === 'retainers' && (
        <Card>
          <div className="fin-toolbar">
            <div>
              <h3 className="fin-h">Fixed recurring fees</h3>
              <p className="fin-sub">
                A standing charge billed by the calendar — "₹15,000 a month" — regardless of what was filed.
                Invoices are generated in arrears once a period has elapsed, as drafts for you to check.
                {' '}
                <Link to="/org-admin/retainers">Open the Retainers section</Link> for the services each
                one covers, what was agreed against the firm's rate card, and the agreement paper.
              </p>
            </div>
            <span style={{ flex: 1 }} />
            <Button variant="primary" onClick={() => setEditingRetainer({})}>+ Add retainer</Button>
          </div>

          {loading || !retainers ? <Skeleton.Text lines={5} /> : (
            <>
              <p className="fin-note">
                {retainers.totals.active} active · <b>{money(retainers.totals.annualValue)}</b> a year
              </p>
              <div className="fin-scroll">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Client</th><th>Title</th><th>Covers</th>
                      <th className="fin-num">Amount</th><th>Every</th>
                      <th className="fin-num">Per year</th>
                      <th>Last billed</th><th>Status</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {retainers.rows.map((r) => (
                      <tr key={r.id}>
                        <td><span className="fin-name">{r.clientName}</span>{r.clientCode && <span className="fin-code">{r.clientCode}</span>}</td>
                        <td>{r.title}</td>
                        <td className="fin-muted">
                          {r.servicesCount
                            ? `${r.servicesCount} service${r.servicesCount === 1 ? '' : 's'}`
                            : 'Flat fee'}
                        </td>
                        <td className="fin-num"><b>{money(r.amount)}</b></td>
                        <td className="fin-muted">{r.frequency.replace('_', ' ')}</td>
                        <td className="fin-num fin-muted">{money(r.annualValue)}</td>
                        <td className="fin-muted">{r.lastBilledPeriod || '—'}</td>
                        <td>
                          <span className={`fin-pill ${r.isActive ? 'fin-s-paid' : 'fin-s-cancelled'}`}>
                            {r.isActive ? 'Active' : 'Stopped'}
                          </span>
                        </td>
                        <td className="fin-num">
                          <Button variant="secondary" size="sm" onClick={() => setEditingRetainer(r)}>Edit</Button>
                        </td>
                      </tr>
                    ))}
                    {retainers.rows.length === 0 && (
                      <tr><td colSpan={9}><p className="fin-empty">No fixed fees set up yet.</p></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}

      {drawerClient && (
        <ClientFinanceDrawer
          clientId={drawerClient}
          onClose={() => setDrawerClient(null)}
          onOpenInvoice={(id) => { setDrawerClient(null); setDrawerInvoice(id); }}
        />
      )}
      {drawerInvoice && (
        <InvoiceDrawer
          invoiceId={drawerInvoice}
          onClose={() => setDrawerInvoice(null)}
          onChanged={() => { bump(); }}
        />
      )}
      {building && (
        <InvoiceBuilder
          preset={building}
          onClose={() => setBuilding(null)}
          // Clear the tick list: those charges are on an invoice now, and a
          // stale selection would make the next "Invoice (3)" button lie.
          onCreated={(id) => { setBuilding(null); setPicked(new Set()); bump(); setDrawerInvoice(id); }}
        />
      )}
      {editingRetainer && (
        <RetainerEditor
          retainer={editingRetainer}
          onClose={() => setEditingRetainer(null)}
          onSaved={() => { setEditingRetainer(null); bump(); }}
        />
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminFinance;
